import dayjs from 'dayjs';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import {
  getUserNameChangeExample,
  USER_NAME_CHANGE_DATE_FORMAT,
  USER_NAME_CHANGE_EXAMPLE_EMAIL,
} from '../decom-policy-utils.tsx';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface UserNameChangeStepProps {
  control: Control<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  day: number;
  idx: number;
}

export default function UserNameChangeStep({
  control,
  watch,
  setValue,
  day,
  idx,
}: UserNameChangeStepProps) {
  const action = watch(`actions.${day}.actions.${idx}`);
  const addDate = action?.addDate === true;
  const showExample = Boolean(action?.preName || action?.postName || addDate);

  const handleAddDateChange = (checked: boolean) => {
    setValue(`actions.${day}.actions.${idx}.addDate`, checked, {
      shouldDirty: true,
    });
    if (checked && !action?.datePosition) {
      setValue(`actions.${day}.actions.${idx}.datePosition`, 'prefix', {
        shouldDirty: true,
      });
    }
  };

  return (
    <Stack gap={1}>
      <Stack flexDirection={'row'} gap={2}>
        <Controller
          name={`actions.${day}.actions.${idx}.preName`}
          control={control}
          render={({ field }) => (
            <TextField
              value={field.value || ''}
              label="Prefix"
              onChange={(e) => field.onChange(e.target.value)}
              sx={{ flexGrow: 1 }}
              autoComplete="off"
            />
          )}
        />
        <Controller
          name={`actions.${day}.actions.${idx}.postName`}
          control={control}
          render={({ field }) => (
            <TextField
              value={field.value || ''}
              label="Suffix"
              onChange={(e) => field.onChange(e.target.value)}
              sx={{ flexGrow: 1 }}
              autoComplete="off"
            />
          )}
        />
      </Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={addDate}
            onChange={(e) => handleAddDateChange(e.target.checked)}
          />
        }
        label="Add decommission date"
      />
      {addDate ? (
        <Controller
          name={`actions.${day}.actions.${idx}.datePosition`}
          control={control}
          render={({ field }) => (
            <FormControl sx={{ pl: 4 }}>
              <FormLabel id="user-name-change-date-position-label">
                Add date to
              </FormLabel>
              <RadioGroup
                aria-labelledby="user-name-change-date-position-label"
                name="user-name-change-date-position"
                value={field.value ?? 'prefix'}
                onChange={(e) => field.onChange(e.target.value)}
                row
              >
                <FormControlLabel
                  value="prefix"
                  control={<Radio />}
                  label="Prefix"
                />
                <FormControlLabel
                  value="suffix"
                  control={<Radio />}
                  label="Suffix"
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      ) : null}
      {showExample ? (
        <Alert severity="info" icon={false} sx={{ width: '100%' }}>
          <AlertTitle>Example</AlertTitle>
          <Typography variant="body2">
            {USER_NAME_CHANGE_EXAMPLE_EMAIL}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {' '}
            {getUserNameChangeExample(
              action,
              dayjs().format(USER_NAME_CHANGE_DATE_FORMAT)
            )}
          </Typography>
          {addDate ? (
            <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
              The date is set to the day the policy runs, in the domain's
              timezone.
            </Typography>
          ) : null}
        </Alert>
      ) : null}
    </Stack>
  );
}
